/**
 * Plan meals and default habits are stored once per duo, so each person sees them in their own
 * language at display time: template dishes and default habits are matched by their stored text.
 * Anything the couple or the coach wrote themselves is shown as written.
 */
import template from '../data/template.json';
import names from '../data/template.i18n.json';
import { getLang, t } from './i18n';

type Track = 'H' | 'A';
type Dish = { name: string; H: { d: string }; A: { d: string } };
const I = names as Record<string, { en: string; tr: string; H: string; A: string }>;
const tpl = template as unknown as { breakfasts: Record<string, Dish>; snacks: Record<string, Dish>; mains: Record<string, Dish>; cheat: Dish };

const byName = new Map<string, string>();
const byPortion = new Map<string, [string, Track]>();
for (const [key, d] of [['cheat', tpl.cheat] as [string, Dish], ...Object.entries({ ...tpl.breakfasts, ...tpl.snacks, ...tpl.mains })]) {
  byName.set(d.name.replace(' 🎉', ''), key);
  byPortion.set(d.H.d, [key, 'H']);
  byPortion.set(d.A.d, [key, 'A']);
}

export function mealName(name: string): string {
  const key = byName.get(name);
  return key && I[key] ? I[key][getLang()] : name;
}

/** Portion text; scaled portions look like "Portions ×1.07: <template text>". */
export function portionText(d: string): string {
  if (getLang() === 'en') return d;
  const m = d.match(/^Portions ×([\d.]+): ([\s\S]*)$/);
  const hit = byPortion.get(m ? m[2] : d);
  if (!hit) return d;
  const text = I[hit[0]]?.[hit[1]] ?? (m ? m[2] : d);
  return m ? `Porsiyonlar ×${m[1].replace('.', ',')}: ${text}` : text;
}

// Sentences the plan builder writes into meal notes (web/src/lib/plan.ts noteFor).
const NOTE_PARTS = [
  'Drink 500 ml water first. Eat slowly, stop at satisfied, walk after. Back on plan at Sunday breakfast; no skipping meals to make up for it.',
  'Training tonight: eat this 60–90 minutes before class.',
  'Right after your morning class.',
  'Leftovers from last night (cooked double). Pack it if you are at work.',
  'From Sunday meal prep. Lunchbox if you are at work.',
  'Post-workout lunch: fresh and quick.',
  'Fresh lunch. Soup keeps 3 days, so make a big pot.',
  'Lunchbox if you are at work.',
  'Use the chicken grilled at Sunday prep. A 10-minute dinner after class.',
  "Cook double: the second half is tomorrow's lunch.",
  'Fish night: buy it fresh today.',
];

export function mealNote(note: string): string {
  if (!note || getLang() === 'en') return note;
  let out = note;
  for (const p of NOTE_PARTS) out = out.split(p).join(t(p));
  return out;
}

// Default habits (copy of functions/src/defaults.ts, same order): [English title, English hint, Turkish title, Turkish hint].
const HABITS: [string, string, string, string][] = [
  ['Wake-up water', '500 ml water with lemon before any tea or coffee.', 'Uyanış suyu', 'Çay ya da kahveden önce limonlu 500 ml su.'],
  ['Water check', 'Drink {waterShare} since the last check. A 750 ml bottle on the desk makes counting easy.', 'Su kontrolü', "Son kontrolden beri {waterShare} iç. Masada 750 ml'lik bir şişe saymayı kolaylaştırır."],
  ['Last caffeine + water', 'Last çay or coffee of the day, no sugar. Then {waterShare} of water.', 'Son kafein + su', 'Günün son çayı ya da kahvesi, şekersiz. Sonra {waterShare} su.'],
  ['Water check', '{waterShare} since the last check. Add 500 ml on training days.', 'Su kontrolü', 'Son kontrolden beri {waterShare}. Antrenman günlerinde 500 ml ekle.'],
  ['Herbal tea · kitchen closed', 'Ihlamur, papatya or adaçayı, no sugar. Nothing else to eat after this.', 'Bitki çayı · mutfak kapandı', 'Ihlamur, papatya ya da adaçayı, şekersiz. Bundan sonra başka bir şey yeme.'],
  ['Walk after lunch', '20 minutes outside, or 10 minutes near the office. It is the cheapest fat-loss tool you have.', 'Öğle sonrası yürüyüş', 'Dışarıda 20 dakika ya da ofis yakınında 10 dakika. Elindeki en ucuz yağ yakma aracı.'],
  ['Walk together after dinner', '15 minutes at an easy pace. Daily step goal: {steps}.', 'Akşam yemeği sonrası birlikte yürüyüş', 'Rahat tempoda 15 dakika. Günlük adım hedefi: {steps}.'],
  ['Weekly meal prep', 'Grill ~900 g chicken breast, cook a pot of bulgur, boil 8 eggs, chop salad veg, portion yoğurt into pots.', 'Haftalık yemek hazırlığı', '~900 g tavuk göğsü ızgara yap, bir tencere bulgur pişir, 8 yumurta haşla, salata sebzelerini doğra, yoğurdu kaplara böl.'],
  ['Weekly weigh-in', 'After the toilet, before water and breakfast, same scale. Log it in Progress.', 'Haftalık tartı', "Tuvaletten sonra, su ve kahvaltıdan önce, aynı tartıda. İlerleme'ye kaydet."],
  ['Groceries', "Use this week's list in Shop. Compare markets for the best indirim first.", 'Market alışverişi', "Alışveriş'teki bu haftanın listesini kullan. Önce marketlerdeki indirimleri karşılaştır."],
  ['Body scan', 'Same scale, before breakfast. Log weight, body fat, visceral fat and muscle in Progress.', 'Vücut analizi', "Aynı tartı, kahvaltıdan önce. Kilo, vücut yağı, viseral yağ ve kası İlerleme'ye kaydet."],
  ['', 'Drink 250 ml before and 250 ml after. Eat your pre-workout snack 60–90 minutes before.', '', 'Öncesinde 250 ml, sonrasında 250 ml su iç. Antrenman öncesi ara öğününü 60–90 dakika önce ye.'],
];
const habitTitle = new Map<string, [string, string]>();
const habitHint = new Map<string, [string, string]>();
for (const [et, eh, tt, th] of HABITS) {
  if (et) { habitTitle.set(et, [et, tt]); habitTitle.set(tt, [et, tt]); }
  habitHint.set(eh, [eh, th]); habitHint.set(th, [eh, th]);
}
const pick = (pair: [string, string] | undefined, s: string) => (pair ? pair[getLang() === 'en' ? 0 : 1] : s);
export const habitTitleText = (s: string) => pick(habitTitle.get(s), s);
export const habitHintText = (s: string | undefined) => (s ? pick(habitHint.get(s), s) : s);
